/**
 * When Segment is not loaded in a deployment, use function stubs to prevent TypeErrors.
 */
const ANALYTICS_STUB = {
  trackSubmit: () => {},
  trackClick: () => {},
  trackLink: () => {},
  trackForm: () => {},
  pageview: () => {},
  identify: () => {},
  reset: () => {},
  group: () => {},
  track: () => {},
  ready: () => {},
  alias: () => {},
  debug: () => {},
  page: () => {},
  screen: () => {},
  once: () => {},
  off: () => {},
  on: () => {},
  addSourceMiddleware: () => {},
  addIntegrationMiddleware: () => {},
  setAnonymousId: () => {},
  addDestinationMiddleware: () => {},
  register: () => {},
};

/**
 * The SegmentService wraps the Segment analytics library and provides a
 * higher-level interface for sending analytics events. All invocations of
 * `window.analytics` should be encapsulated within this service, to ensure that
 * we can centralize any API updates or breaking changes.
 *
 * To learn more about our implementation of Segment, see docs at
 * https://bridgecare.atlassian.net/l/cp/CHEHV1PL
 */
export default class SegmentService {
  constructor(portal, analytics, amplitudeEngagement = null) {
    this.portal = portal;
    this.analytics = analytics ? analytics : ANALYTICS_STUB;
    this.amplitudeEngagement = amplitudeEngagement;
  }

  static forParent(analytics, amplitudeEngagement = null) {
    return new SegmentService('Parent', analytics, amplitudeEngagement);
  }

  static forProvider(analytics, amplitudeEngagement = null) {
    return new SegmentService('Provider', analytics, amplitudeEngagement);
  }

  static forSpecialist(analytics, amplitudeEngagement = null) {
    return new SegmentService('Specialist', analytics, amplitudeEngagement);
  }

  /**
   * Gets organization context consistently across all call types.
   * Checks properties/params first, then falls back to window variables.
   *
   * @param {Object} options - Options object that may contain organization_id/name
   * @returns {Object} Object with organization_id and organization_name
   */
  _getOrganizationContext(options = {}) {
    return {
      organization_id: options.organization_id || window.organization_id,
      organization_name: options.organization_name || window.organization_name,
    };
  }

  /**
   * Tracks a page navigation. Generally, this should only be called from a Vue
   * Router navigation callback at the top level of the application.
   *
   * @param {string} pageName - Name of the page
   * @param {string} category - Optional category of the page
   * @param {Object} properties - Optional properties object (will have organization_id added)
   * @param {Object} options - Optional Segment options
   */
  page(pageName, category = null, properties = {}, options = {}) {
    const orgContext = this._getOrganizationContext(properties);

    const pageProperties = {
      ...properties,
      organization_id: orgContext.organization_id,
      organization_name: orgContext.organization_name,
    };

    // Forward page view to Amplitude Engagement if available
    if (this.amplitudeEngagement) {
      // Amplitude Engagement expects track events, so we convert page views to track events
      const eventName = category ? `${category} - ${pageName}` : pageName;
      this.amplitudeEngagement.forwardEvent(`Page Viewed - ${eventName}`, pageProperties);
    }

    this.analytics.page(`${this.portal}::${pageName}`, category, pageProperties, options);
  }

  /**
   * Saves a user record to Segment CDP. This should be called any time any user
   * successfully logs in to a session. This method should be provided with data
   * from the Member record.
   *
   * Note: If the login process will reload the page after a successful login,
   * this method must be awaited before the reload otherwise the identify call
   * will not be sent. Awaiting is not necessary if the login process performs a
   * client-side navigation.
   */
  async identifyMember(profile = {}) {
    if (!profile || typeof profile !== 'object' || !profile.id || !profile.organization_id) {
      return;
    }

    const {
      business_name: business,
      name,
      email,
      id: memberId,
      organization_id: organizationId,
      organization_name: organization,
    } = profile;

    // Inform Segment who this user is and what organization they belong to
    await Promise.allSettled([
      this.analytics.identify(memberId, {
        name,
        email,
        business,
        organization,
        organization_id: organizationId,
        organization_name: organization,
      }),
      this.analytics.group(organizationId, {
        name: organization,
        organization_id: organizationId,
        organization_name: organization,
      }),
    ]);
  }

  /*
  Runs a track for a custom event. The only required parameter is eventName.
  Segment recommends event names built from a noun and past-tense verb, and they
  be specific.
  Question 12 Back Button Clicked >  Button Click

  Properties are arbitrary values passed into the track call, likely specific
  to each track event. Likely some identifying attributes like a program id,
  user id, organization id, if certain options were enabled, etc.

  Options are Segment tracking options, one example being downstream integrations
  to enable or disable for the call:
  {integrations: {'All': false, 'Intercom': true}}

  The callback is to be run after the request finishes, or 300ms. Use to prevent
  navigating away or changing the page too much before the event is properly
  tracked.
  */
  async track(eventName, properties = {}, options = {}, callback) {
    const orgContext = this._getOrganizationContext(properties);

    const eventProperties = {
      ...properties,
      organization_id: orgContext.organization_id,
      organization_name: orgContext.organization_name,
      window_url: window.location.href,
      window_pathname: window.location.pathname,
      window_search: window.location.search,
    };

    // Forward event to Amplitude Engagement if available
    if (this.amplitudeEngagement) {
      this.amplitudeEngagement.forwardEvent(eventName, eventProperties);
    }

    await this.analytics.track(eventName, eventProperties, options, callback);
  }
}
